import { AlertTriangle, RefreshCw } from 'lucide-react';
import type { FallbackProps } from 'react-error-boundary';
import { useT } from '../i18n/useT';
import { ApiError } from '../lib/apiClient';
import { Button } from './ui/Button';

/**
 * Hata sınırı arayüzü.
 *
 * Prototipte hiç Error Boundary yoktu: tek bir bileşende oluşan render hatası
 * (örneğin `sess.bookedCount` gibi tanımsız bir alana erişim zinciri) tüm
 * uygulamayı beyaz ekrana düşürüyordu ve kullanıcının elinde hiçbir bilgi
 * kalmıyordu.
 *
 * Metinler sözlükten: iki sınır da `LocaleProvider` İÇİNDE monte ediliyor
 * (`main.tsx`), dolayısıyla `useT` burada güvenli.
 */

/** Uygulama kökü — her şey çöktüğünde. */
export function AppErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const t = useT();
  const requestId = error instanceof ApiError ? error.requestId : undefined;

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-6">
      <div className="w-full max-w-md rounded-sm border border-line bg-surface p-8 text-center shadow-sm">
        <AlertTriangle aria-hidden className="mx-auto mb-4 h-10 w-10 text-warn" />
        <h1 className="mb-2 font-serif text-2xl text-ink">{t.errorFallback.title}</h1>
        <p className="mb-6 text-sm text-ink-2">{t.errorFallback.body}</p>

        {requestId && (
          // Destek ekibi bu kimlikle sunucu loglarındaki tam kaydı bulabilir.
          <p className="mb-6 rounded-sm bg-sunken px-3 py-2 font-mono text-[11px] text-ink-3">
            {t.errorFallback.supportCode(requestId)}
          </p>
        )}

        <div className="flex justify-center gap-3">
          <Button intent="outline" size="sm" onClick={resetErrorBoundary}>
            {t.errorFallback.retry}
          </Button>
          <Button intent="primary" size="sm" onClick={() => window.location.reload()}>
            <RefreshCw className="h-3.5 w-3.5" />
            {t.errorFallback.reload}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Panel düzeyi sınır — bir sekme çökerse gezinme ve diğer paneller ayakta kalır.
 * Hata izolasyonu, hata yakalamak kadar önemlidir.
 */
export function PanelErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const t = useT();
  const message = error instanceof Error ? error.message : t.errorFallback.unknown;

  return (
    <div
      role="alert"
      className="rounded-sm border border-risk bg-risk-soft/40 p-8 text-center"
    >
      <AlertTriangle aria-hidden className="mx-auto mb-3 h-7 w-7 text-risk" />
      <p className="mb-1 text-sm font-semibold text-risk">{t.errorFallback.panelTitle}</p>
      <p className="mx-auto mb-5 max-w-md text-xs text-risk/80">{message}</p>
      <Button intent="outline" size="sm" onClick={resetErrorBoundary}>
        {t.errorFallback.retry}
      </Button>
    </div>
  );
}
